import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import RenderToast from "../../../components/RenderToast";
import API from "../../../utils/api";
import styles from "./AddCategory.module.css";

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function AddCategory() {
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [parentId, setParentId] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await API.post("/categories.php", { action: "fetch" });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const normalized = data.map((cat) => ({
        ...cat,
        is_uncategorized: Number(cat.is_uncategorized) === 1,
      }));
      setCategories(normalized.filter((cat) => !cat.is_uncategorized));
    } catch (err) {
      console.error(err);
      RenderToast({
        type: "error",
        message: "Unable to load categories. Please refresh.",
      });
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(
    () => setSlug(categoryName ? slugify(categoryName) : ""),
    [categoryName]
  );

  const parentOptions = useMemo(() => {
    const parentMap = {};
    categories.forEach((cat) => {
      if (!cat.parent_id) {
        parentMap[cat.id] = { ...cat, children: [] };
      }
    });
    categories.forEach((cat) => {
      if (cat.parent_id && parentMap[cat.parent_id]) {
        parentMap[cat.parent_id].children.push(cat);
      }
    });
    const ordered = [];
    Object.values(parentMap).forEach((parent) => {
      ordered.push({
        id: parent.id,
        label: parent.categoryName,
      });
      parent.children.forEach((child) =>
        ordered.push({
          id: child.id,
          label: `${parent.categoryName} → ${child.categoryName}`,
        })
      );
    });
    return ordered;
  }, [categories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      return RenderToast({
        type: "error",
        message: "Category name is required",
      });
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      return RenderToast({ type: "error", message: "Please login first" });
    }

    try {
      setSaving(true);
      const formData = new URLSearchParams();
      formData.append("action", "add");
      formData.append("category_name", categoryName.trim());
      formData.append("parent_id", parentId || "");
      formData.append("slug", slug);
      formData.append("description", description.trim());

      const res = await fetch(`${API.base}/categories.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        RenderToast({
          type: "success",
          message: "Category added successfully!",
        });
        setCategoryName("");
        setParentId("");
        setDescription("");
        setSlug("");
        fetchCategories();
      } else {
        RenderToast({
          type: "error",
          message: data.error || "Failed to add category",
        });
      }
    } catch (err) {
      RenderToast({
        type: "error",
        message: "Network error. Please try again.",
      });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <p className={styles.kicker}>Category Manager</p>
            <h1 className={styles.title}>Add New Category</h1>
            <p className={styles.subtitle}>
              Create structured parent-child categories. Slug is generated
              automatically and kept read-only to avoid mistakes.
            </p>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.ghostBtn}
              onClick={() => {
                setCategoryName("");
                setParentId("");
                setDescription("");
                setSlug("");
                RenderToast({
                  type: "info",
                  message: "Form cleared",
                });
              }}
              aria-label="Clear form"
            >
              Clear Form
            </button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className={styles.formCard} noValidate>
          <div className={styles.gridRow}>
            <label className={styles.formField}>
              <span className={styles.labelText}>Category Name *</span>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="e.g. Storage Device"
                required
                aria-required
                aria-label="Category Name"
              />
            </label>

            <label className={styles.formField}>
              <span className={styles.labelText}>Slug</span>
              <input
                type="text"
                value={slug}
                disabled
                aria-disabled
                aria-label="Slug (read-only)"
              />
              <small className={styles.helpText}>
                Slug is generated from the name and cannot be edited.
              </small>
            </label>
          </div>

          <div className={styles.gridRow}>
            <label className={styles.formField}>
              <span className={styles.labelText}>Parent Category</span>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                aria-label="Parent Category"
              >
                <option value="">None (Top level)</option>
                {parentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.formField}>
              <span className={styles.labelText}>Description (optional)</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Write a short summary for this category..."
                aria-label="Category Description"
              />
            </label>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => {
                setCategoryName("");
                setParentId("");
                setDescription("");
                setSlug("");
              }}
              disabled={saving}
            >
              Reset
            </button>

            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={saving}
              aria-busy={saving}
            >
              {saving ? (
                <>
                  <span className={styles.spinner} aria-hidden />
                  Saving...
                </>
              ) : (
                "Add Category"
              )}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
